/**
 * useDisposicaoPendentes — contador de peças aguardando disposição do PCP
 * (Lote D — §2.7) para o badge da sidebar em Produção > Disposição.
 *
 * Estratégia (espelho leve do padrão useWhatsappNaoLidas):
 *   - Só roda com sessão autenticada + `ppcp:view` (senão zero requests).
 *   - Polling lento (2 min) — fila de disposição muda em ritmo humano.
 *   - Cache module-level dedupe montagens simultâneas (item expandido +
 *     Favoritos) e o evento `disposicao-pecas-mudou` força re-leitura
 *     imediata após uma decisão na tela da fila.
 *   - Backend sem o endpoint (Lote D não deployado) ou falha de rede:
 *     mantém o último total conhecido — degradação silenciosa.
 */
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { filaDisposicao } from "../services/producaoV2";

const POLL_MS = 120_000;
const DEDUPE_MS = 30_000;

export const EVENTO_DISPOSICAO_MUDOU = "disposicao-pecas-mudou";

let cache = { total: 0, em: 0 };

/** Notifica os badges montados que a fila mudou (chamar após decidir). */
export function notificarDisposicaoMudou() {
  window.dispatchEvent(new CustomEvent(EVENTO_DISPOSICAO_MUDOU));
}

export default function useDisposicaoPendentes({ enabled = true } = {}) {
  const { request, hasPermission, authenticated } = useAuth();
  const ativo =
    Boolean(enabled) && Boolean(authenticated) && hasPermission("ppcp:view");
  const [total, setTotal] = useState(cache.total);

  const carregar = useCallback(
    async (forcar = false) => {
      if (!ativo) return;
      if (!forcar && Date.now() - cache.em < DEDUPE_MS) {
        setTotal(cache.total);
        return;
      }
      try {
        const payload = await filaDisposicao(request);
        const qtd = Array.isArray(payload?.pecas) ? payload.pecas.length : 0;
        cache = { total: qtd, em: Date.now() };
        setTotal(qtd);
      } catch {
        // Endpoint ausente (backend atrás) ou rede — mantém o último total.
      }
    },
    [ativo, request],
  );

  useEffect(() => {
    if (!ativo) return undefined;
    carregar();
    const timer = setInterval(() => carregar(true), POLL_MS);
    const aoMudar = () => carregar(true);
    window.addEventListener(EVENTO_DISPOSICAO_MUDOU, aoMudar);
    return () => {
      clearInterval(timer);
      window.removeEventListener(EVENTO_DISPOSICAO_MUDOU, aoMudar);
    };
  }, [ativo, carregar]);

  return total;
}
